import { Pipe, PipeTransform } from '@angular/core';

/** Tabella markdown del modello: celle già in HTML inline (escapato), più il testo semplice per la copia. */
export interface IAssistantTable {
	/** frase che precede la tabella (titolo della finestra a schermo intero), senza i due punti finali */
	caption: string | null;
	header: string[];
	rows: string[][];
	/** colonne di importi/numeri: allineate a destra */
	numeric: boolean[];
	plain: { header: string[]; rows: string[][] };
}

export type AssistantSegment = { kind: 'html'; html: string } | { kind: 'table'; table: IAssistantTable };

/**
 * Markdown minimo delle risposte del modello → HTML: paragrafi, elenchi (- / * / 1.), **grassetto**, *corsivo*.
 * L'input viene prima escapato: nessun HTML del modello arriva al DOM (e [innerHTML] sanifica comunque).
 * Le tabelle (| a | b | + riga |---|) le separa segments(): il pannello le rende con un componente apposta.
 */
@Pipe({
	name: 'assistantMarkdown',
	standalone: false
})
export class AssistantMarkdownPipe implements PipeTransform{

	transform(value: string | null | undefined): string{
		if(!value) return '';
		const blocks: string[] = [];
		let list: { tag: 'ul' | 'ol'; items: string[] } | null = null;
		let paragraph: string[] = [];

		const flushParagraph = () => {
			if(paragraph.length) blocks.push('<p>' + paragraph.join('<br>') + '</p>');
			paragraph = [];
		};
		const flushList = () => {
			if(list) blocks.push('<' + list.tag + '>' + list.items.map(item => '<li>' + item + '</li>').join('') + '</' + list.tag + '>');
			list = null;
		};

		for(const rawLine of value.replace(/\r\n/g, '\n').split('\n')){
			const line = rawLine.trim();
			const bullet = line.match(/^[-*•]\s+(.*)$/);
			const numbered = line.match(/^\d+[.)]\s+(.*)$/);
			if(bullet || numbered){
				flushParagraph();
				const tag = bullet ? 'ul' : 'ol';
				if(list && list.tag !== tag) flushList();
				if(!list) list = { tag, items: [] };
				list.items.push(AssistantMarkdownPipe.inline((bullet || numbered)[1]));
				continue;
			}
			flushList();
			if(!line){
				flushParagraph();
				continue;
			}
			paragraph.push(AssistantMarkdownPipe.inline(line));
		}
		flushParagraph();
		flushList();
		return blocks.join('');
	}

	/**
	 * Testo diviso in blocchi HTML e tabelle, nell'ordine. Una tabella vale solo con la riga separatrice sotto
	 * l'intestazione: righe con "|" senza separatore restano testo.
	 */
	static segments(value: string | null | undefined): AssistantSegment[]{
		if(!value) return [];
		const lines = value.replace(/\r\n/g, '\n').split('\n');
		const segments: AssistantSegment[] = [];
		let text: string[] = [];
		const flushText = () => {
			const html = new AssistantMarkdownPipe().transform(text.join('\n'));
			if(html) segments.push({ kind: 'html', html });
			text = [];
		};

		for(let index = 0; index < lines.length; index++){
			if(!AssistantMarkdownPipe.isTableRow(lines[index]) || !AssistantMarkdownPipe.isSeparator(lines[index + 1])){
				text.push(lines[index]);
				continue;
			}
			const header = AssistantMarkdownPipe.cells(lines[index]);
			const rows: string[][] = [];
			index += 2;
			while(index < lines.length && AssistantMarkdownPipe.isTableRow(lines[index])){
				const cells = AssistantMarkdownPipe.cells(lines[index]);
				rows.push(header.map((_, column) => cells[column] ?? ''));
				index++;
			}
			index--;
			const caption = AssistantMarkdownPipe.caption(text);
			flushText();
			const plain = (cell: string) => cell.replace(/\*\*(.+?)\*\*/g, '$1').replace(/(^|[^*])\*(?!\s)(.+?)\*(?!\*)/g, '$1$2');
			segments.push({ kind: 'table', table: {
				caption,
				header: header.map(cell => AssistantMarkdownPipe.inline(cell)),
				rows: rows.map(row => row.map(cell => AssistantMarkdownPipe.inline(cell))),
				numeric: header.map((_, column) => rows.length > 0 && rows.every(row => !row[column] || AssistantMarkdownPipe.isNumeric(plain(row[column])))),
				plain: { header: header.map(plain), rows: rows.map(row => row.map(plain)) }
			} });
		}
		flushText();
		return segments;
	}

	private static isTableRow(line: string | undefined): boolean{
		const trimmed = (line ?? '').trim();
		return trimmed.startsWith('|') && trimmed.length > 1;
	}

	private static isSeparator(line: string | undefined): boolean{
		return /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?$/.test((line ?? '').trim());
	}

	private static cells(line: string): string[]{
		return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(cell => cell.trim());
	}

	private static isNumeric(cell: string): boolean{
		return /^[-+−]?\s*(€\s*)?[\d.,\s]+(\s*(€|%))?$/.test(cell.trim());
	}

	/** ultima riga di testo non vuota prima della tabella, se è una frase introduttiva (finisce con ":") */
	private static caption(text: string[]): string | null{
		const last = [...text].reverse().find(line => line.trim());
		if(!last || !last.trim().endsWith(':')) return null;
		return last.trim().replace(/:$/, '').replace(/\*\*(.+?)\*\*/g, '$1').replace(/^[-*•]\s+/, '');
	}

	static escape(text: string): string{
		return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
	}

	static inline(text: string): string{
		return AssistantMarkdownPipe.escape(text)
			.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
			.replace(/(^|[^*])\*(?!\s)(.+?)\*(?!\*)/g, '$1<em>$2</em>');
	}
}

/**
 * Risposta del modello in blocchi (HTML / tabella) per il template del pannello. Pura: ricalcolata solo se cambia il testo.
 */
@Pipe({
	name: 'assistantSegments',
	standalone: false
})
export class AssistantSegmentsPipe implements PipeTransform{

	transform(value: string | null | undefined): AssistantSegment[]{
		return AssistantMarkdownPipe.segments(value);
	}
}
